import { z } from 'zod';
import { US_STATE_CODES } from '../enums';
import { digits, objectId } from './common';
import { zip5 } from './configuration';
import { imeiNumber, simNumber } from './inventory';
import { PORTOUT_NETWORKS, SHIPPERS } from './screens';

/**
 * Actions on orders after the enrollment wizard: fraud review, shipment, equipment requests
 * and number porting. The reference's dialogs for these were seen by name and field list only
 * (product inventory, appendix parts 1 and 2); what each action does here is described in
 * docs/technical/orders.md.
 */

const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/* ----------------------- Carrier check service availability ---------------- */

/** "CSA CHECK" (reference PC816): Carrier and Zip Code. */
export const coverageCheckSchema = z.object({
  carrierId: objectId,
  zip: zip5,
  /** Which service area program the ZIP is checked for; Lifeline's page sends `lifeline`. */
  program: z.enum(['lifeline', 'nonLifeline']).optional(),
});
export type CoverageCheckInput = z.input<typeof coverageCheckSchema>;

export interface CoverageCheckDto {
  zip: string;
  carrier: string;
  /** What the carrier answered. */
  covered: boolean;
  carrierMessage: string;
  /** Whether this company sells in the ZIP (Settings > Service Area). */
  serviceable: boolean;
  serviceAreaMessage: string;
  city: string | null;
  state: string | null;
}

/* ---------------------------------- Review --------------------------------- */

export const reviewDecisionSchema = z
  .object({
    decision: z.enum(['Approved', 'Rejected']),
    reason: z.string().trim().max(250).default(''),
  })
  .refine((v) => v.decision === 'Approved' || v.reason !== '', {
    path: ['reason'],
    message: 'Reason is required',
  });
export type ReviewDecisionInput = z.input<typeof reviewDecisionSchema>;

/* --------------------------------- Shipment -------------------------------- */

/**
 * "Order Fulfillment" (one by one): Enrollment ID, SIM and Device ID. The dialog of the
 * reference also shows State, Plan, Master Agent and Requested Model, which belong to the
 * order, and has "Device ID2" and "Area Code", which are not built: a line is activated from
 * the customer's status page here, where the carrier gives the number.
 */
export const shipmentFulfilSchema = z.object({
  enrollmentId: z.string().trim().min(1, 'Enrollment ID is required').max(20),
  sim: simNumber,
  /** Only for an order that asked for a device model. */
  deviceId: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    imeiNumber.optional(),
  ),
});
export type ShipmentFulfilInput = z.input<typeof shipmentFulfilSchema>;

/** "Scan ESN/SIM for shipment": ESN/SIM* and Shipper*. */
export const shipmentScanSchema = z.object({
  /** The SIM of the order, or the device that was packed with it. */
  sim: digits(14, 22, 'ESN/SIM'),
  shipper: z.enum(SHIPPERS, { required_error: 'Select Shipper' }),
});
export type ShipmentScanInput = z.input<typeof shipmentScanSchema>;

const blank = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

/**
 * "Submit Bulk Shipment" of "View Order Details": Attach SIM and Attach DEVICE for an order
 * that has none yet, and the Shipper the parcel leaves with (release switch
 * `shipping-queue-order-details`).
 */
export const shipmentShipSchema = z.object({
  sim: z.preprocess(blank, simNumber.optional()),
  deviceId: z.preprocess(blank, imeiNumber.optional()),
  shipper: z.enum(SHIPPERS, { required_error: 'Select Shipper' }),
});
export type ShipmentShipInput = z.input<typeof shipmentShipSchema>;

/** "View Order Details": the order's line (one customer is one line here). */
export interface ShipmentOrderDetailDto {
  id: string;
  enrollmentId: string;
  status: string;
  requestedModel: string;
  sim: string;
  deviceId: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  mailing1: string;
  mailing2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  orderType: DropshipOrderType;
  planName: string;
  activatedAt: string | null;
  shipper: string;
}

/** One line of "USPS Address Request and Response": # | Api | Request | Response | Date Time. */
export interface AddressCheckDto {
  id: string;
  api: string;
  request: string;
  response: string;
  at: string;
}

export const shipmentCancelSchema = z.object({
  reason: z.string().trim().min(1, 'Reason is required').max(250),
});

/** One table of the Dropship Orders page: a queue and its waiting orders by state. */
export interface PendingOrdersQueue {
  queue: string;
  rows: Array<{ state: string; count: number }>;
}

/**
 * The order types of Dropship Orders' "Pending Orders", one STATE | COUNT table each, in the
 * reference's order. Orders here are prepaid or postpaid without a subsidy, so three can hold
 * orders: Paid Phones Non Lifeline (prepaid), Post Paid Phones Non Lifeline (postpaid) and For
 * Sim Only (a SIM model). The free-phone (Lifeline), Paid Phones and Family Plan types stay empty.
 */
export const DROPSHIP_ORDER_TYPES = [
  'Free Phones With Data',
  'Free Phones Without Data',
  'Paid Phones',
  'Paid Phones Non Lifeline',
  'Post Paid Phones Non Lifeline',
  'For Sim Only',
  'Family Plan Orders',
] as const;
export type DropshipOrderType = (typeof DROPSHIP_ORDER_TYPES)[number];

/** The orders behind one count of "Pending Orders": an order type and a state. */
export const pendingOrdersQuerySchema = z.object({
  orderType: z.enum(DROPSHIP_ORDER_TYPES, { required_error: 'Select Order Type' }),
  state: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, 'Select State'),
});

export interface PendingOrdersByType {
  orderType: DropshipOrderType;
  rows: Array<{ state: string; count: number }>;
}

/**
 * A line of "View Shipment Order Details": the reference's columns 212Ez keeps data for (no port-in
 * or provisioning columns: a shipment order here is not a port and is provisioned on activation).
 */
export interface PendingShipmentOrderDto {
  id: string;
  enrollmentId: string;
  attachEsn: string;
  status: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  mailing1: string;
  mailing2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  orderType: DropshipOrderType;
  planName: string;
  activatedAt: string | null;
}

/* ---------------------------- Equipment requests --------------------------- */

export const EQUIPMENT_PROCESS_TYPES = ['Equipment Purchase', 'Resend Phone'] as const;
export type EquipmentProcessType = (typeof EQUIPMENT_PROCESS_TYPES)[number];

/**
 * Payment methods of the reference's Equipment Purchase screen that need no payment gateway,
 * each with the reference's own code. Credit Card (PC408) and Dealer Wallet (PC544) are not
 * built.
 */
export const EQUIPMENT_PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', code: 'PC409' },
  { key: 'moneyOrder', label: 'Money Order', code: 'PC410' },
  { key: 'moneyGram', label: 'Money Gram', code: 'PC411' },
  { key: 'wallet', label: 'Wallet', code: 'PC412' },
  { key: 'free', label: 'Free Equipment Purchase', code: 'PC572' },
] as const;
/** Methods that come with a receipt from the customer. */
export const EQUIPMENT_RECEIPT_METHODS: readonly string[] = ['cash', 'moneyOrder', 'moneyGram'];
export type EquipmentPaymentMethod = (typeof EQUIPMENT_PAYMENT_METHODS)[number]['key'];

export const equipmentPurchaseSchema = z
  .object({
    customerId: objectId,
    equipmentId: objectId,
    processType: z.enum(EQUIPMENT_PROCESS_TYPES).default('Equipment Purchase'),
    /** An equipment status name ("Default", "Never Received", ...). */
    reasonId: z.preprocess(blankToUndefined, objectId.optional()),
    method: z.enum(['cash', 'moneyOrder', 'moneyGram', 'wallet', 'free']),
    receiptNumber: z.string().trim().max(30).default(''),
  })
  .refine((v) => !EQUIPMENT_RECEIPT_METHODS.includes(v.method) || v.receiptNumber !== '', {
    path: ['receiptNumber'],
    message: 'Receipt Number is required',
  });
export type EquipmentPurchaseInput = z.input<typeof equipmentPurchaseSchema>;

export const equipmentRequestCompleteSchema = z.object({
  shipper: z.enum(SHIPPERS, { required_error: 'Select Shipper' }),
  trackingNumber: z.string().trim().max(40).default(''),
});

export interface EquipmentOption {
  id: string;
  name: string;
  type: string;
  priceCents: number;
}

export interface EquipmentRequestDto {
  id: string;
  equipment: string;
  equipmentType: string;
  processType: EquipmentProcessType;
  reason: string;
  /** What was charged: the price with its tax. */
  amountCents: number;
  taxCents: number;
  method: EquipmentPaymentMethod;
  receiptNumber: string;
  status: 'Pending Order' | 'Completed Order';
  shipper: string;
  trackingNumber: string;
  requestedAt: string;
  completedAt: string | null;
}

/* ---------------------------------- Port in -------------------------------- */

export const PORTIN_TYPES = ['Port-in with Existing SIM', 'Port-in with New SIM'] as const;
export type PortInType = (typeof PORTIN_TYPES)[number];

export const PORTIN_REQUEST_MODES = ['Automatic', 'Manual'] as const;

/** What becomes of the SIM the line leaves when the number moves to a new SIM. */
export const OLD_SIM_FATES = ['Make the old SIM available', 'Deactivate the old SIM'] as const;
/** The reference's wording of each choice (the stored values stay as they were). */
export const OLD_SIM_FATE_LABEL: Record<(typeof OLD_SIM_FATES)[number], string> = {
  'Make the old SIM available': 'Make the old SIM available in the inventory',
  'Deactivate the old SIM': 'Deactivate the old SIM',
};
export const SWAP_TYPES = [
  'Complete swap as soon as port completes',
  'Do not complete Swap Immediately',
] as const;
export const SWAP_TYPE_LABEL: Record<(typeof SWAP_TYPES)[number], string> = {
  'Complete swap as soon as port completes': 'Complete swap as soon as port-in completes',
  'Do not complete Swap Immediately': 'Do not complete Swap Immediately',
};

/**
 * "Existing Subscriber Portin" (reference PC343): brings a number from another provider onto
 * the line of an existing customer. The reference form also takes an SSN and street parts;
 * the SSN is deliberately not collected and the address is kept as lines.
 */
export const portInSchema = z
  .object({
    customerId: objectId,
    portinType: z.enum(PORTIN_TYPES),
    newSim: z.preprocess(blankToUndefined, simNumber.optional()),
    oldSimFate: z.enum(OLD_SIM_FATES).default('Make the old SIM available'),
    /** "Enter to port number": the number that comes in. */
    mdn: digits(10, 10, 'Port number'),
    oldCarrier: z.string().trim().max(60).default(''),
    accountNumber: z.string().trim().min(1, 'Account number is required').max(40),
    /** Sent to the carrier and not stored. */
    passwordPin: z.string().trim().max(20).default(''),
    billFirstName: z.string().trim().min(1, 'Bill First name is required').max(40),
    billLastName: z.string().trim().min(1, 'Bill last name is required').max(40),
    address1: z.string().trim().min(1, 'Address 1 is required').max(120),
    address2: z.string().trim().max(120).default(''),
    city: z.string().trim().min(1, 'City is required').max(60),
    state: z.enum(US_STATE_CODES, { required_error: 'State is required' }),
    zip: zip5,
    swapType: z.enum(SWAP_TYPES).default('Complete swap as soon as port completes'),
    // The reference form's further address fields (kept on the request; not sent to the carrier).
    businessName: z.string().trim().max(60).optional().default(''),
    streetNumber: z.string().trim().max(20).optional().default(''),
    streetType: z.string().trim().max(30).optional().default(''),
    streetDirection: z.string().trim().max(30).optional().default(''),
    newNumLocalSp: z.string().trim().max(15).optional().default(''),
    // Release switch `portin-who-got-it-back`; absent means "as before".
    /** "Who got it back(Employee LoginID)": the old SIM then sits in that user's stock. */
    returnedTo: z.string().trim().max(40).optional().default(''),
    /** "IMEI" (max 30): the device of the line; empty keeps the one the line has. */
    imei: z.string().trim().max(30).optional().default(''),
    /**
     * "Portin Request Type" (seen on the reference's shipper form): Automatic is sent to the
     * carrier; Manual waits in the manual queue for a person to work it at the carrier.
     */
    requestMode: z.enum(PORTIN_REQUEST_MODES).default('Automatic'),
  })
  .refine((v) => v.portinType !== 'Port-in with New SIM' || Boolean(v.newSim), {
    path: ['newSim'],
    message: 'New SIM is required',
  });
export type PortInInput = z.input<typeof portInSchema>;

/**
 * "Update portin" on Modify: the old account's details sent again to the carrier (the reference's
 * fields: OSP account number and password, billing first and last name, street number, street
 * name, street direction, address line 2, city, state code, zip). The password passes through.
 */
export const portInUpdateSchema = z.object({
  accountNumber: z.string().trim().min(1, 'OSP account number is required').max(40),
  passwordPin: z.string().trim().max(20).optional().default(''),
  billFirstName: z.string().trim().min(1, 'First name is required').max(40),
  billLastName: z.string().trim().min(1, 'Last name is required').max(40),
  streetNumber: z.string().trim().max(20).optional().default(''),
  address1: z.string().trim().min(1, 'Street name is required').max(120),
  streetDirection: z.string().trim().max(30).optional().default(''),
  address2: z.string().trim().max(120).optional().default(''),
  city: z.string().trim().min(1, 'City is required').max(60),
  state: z.enum(US_STATE_CODES, { errorMap: () => ({ message: 'State code is required' }) }),
  zip: zip5,
});
export type PortInUpdateInput = z.input<typeof portInUpdateSchema>;

export const portabilityCheckSchema = z.object({
  mdn: digits(10, 10, 'MDN'),
  carrierId: objectId,
});

export const portNoteSchema = z.object({
  text: z.string().trim().min(1, 'Notes is required').max(500),
});

/** Actions of the "Manage portin" dialog. */
export const PORTIN_ACTIONS = [
  'check-status',
  'resubmit',
  'cancel',
  'complete',
  'complete-swap',
] as const;
export const PORTIN_ACTION_LABEL: Record<(typeof PORTIN_ACTIONS)[number], string> = {
  'check-status': 'Check Status',
  resubmit: 'Resubmit',
  cancel: 'Cancel Portin',
  complete: 'Mark Completed',
  'complete-swap': 'Complete Swap',
};

/** The subscriber found by the search box of Existing Subscriber Portin. */
export interface PortSubscriberDto {
  id: string;
  customerId: number;
  enrollmentId: string;
  name: string;
  status: string;
  mdn: string;
  sim: string;
  carrier: { id: string; code: string };
  address: { line1: string; line2: string; city: string; state: string; zip: string };
  /** "Plan name", shown as "name [code]". */
  planLabel?: string;
}
export type PortInAction = (typeof PORTIN_ACTIONS)[number];

export interface PortInDetailDto {
  id: string;
  status: string;
  manual: boolean;
  mdn: string;
  newSim: string;
  oldMdn: string;
  oldSim: string;
  portinType: PortInType;
  swapType: string;
  swapDone: boolean;
  oldCarrier: string;
  accountNumber: string;
  billing: { name: string; address: string };
  /** The billing details field by field, for "Update portin". */
  billingDetails: {
    firstName: string;
    lastName: string;
    streetNumber: string;
    address1: string;
    streetDirection: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  carrierResponse: string;
  attemptCount: number;
  requestedAt: string;
  notes: Array<{ text: string; by: string; at: string }>;
  /** What the caller may do with the request now. */
  actions: PortInAction[];
}

/* --------------------------------- Port out -------------------------------- */

/**
 * A port-out starts at the other carrier, which tells this system through the carrier link.
 * This is the message that link delivers; the simulated link is the API itself.
 */
export const portOutEventSchema = z.object({
  network: z.enum(PORTOUT_NETWORKS),
  mdn: digits(10, 10, 'MDN'),
  gainingCarrier: z.string().trim().max(60).default(''),
});
export type PortOutEventInput = z.input<typeof portOutEventSchema>;

/** "Manage Portout": let the number go, or refuse the request. */
export const portOutDecisionSchema = z
  .object({
    decision: z.enum(['Completed', 'Rejected']),
    reason: z.string().trim().max(250).default(''),
  })
  .refine((v) => v.decision === 'Completed' || v.reason !== '', {
    path: ['reason'],
    message: 'Reason is required',
  });
